// @flow
import React from "react";
import classNames from "classnames";
import Button from "../layout/Button";
import {currencyConverter} from "../../helper/currency";
import type {Product} from "../../models/Product";
import {getGalleryPrice, isStockLimited} from "../../models/Product";
import {DISTRIBUTION} from "../../models/DistributionChannels";
import "./PriceBox.scss";
import type {DealerDetails} from "../../models/DealerDetails";
import PickupLocationContainer from "../../container/pickupLocation/PickupLocationContainer";
import StringUtils from "../../helper/stringUtils";
import PriceInfo from "../layout/PriceInfo";
import {emitInternalEvent, toProductDetails} from "../../helper/tracker/internalEvents";

type PriceBoxProps = {
    t: any;
    infobox: any,
    addToCart?: ({}, string) => void,
    isShopEnabled: boolean,
    isLeadModuleEnabled: boolean,
    watchlistEnabled: boolean,
    productPricesDisabled?: boolean,
    isBookmarked: boolean,
    product: Product,
    dealerDetails: DealerDetails,
    addBookmark: Product => void,
    removeBookmark: Product => void,
    needsPickupLocation: boolean,
    showMsrp: boolean,
}

type PriceBoxState = {
    selectedOption: ?number,
    showInfo: boolean,
}

export default class PriceBox extends React.Component<PriceBoxProps, PriceBoxState> {
    constructor(props: PriceBoxProps) {
        super(props);
        const {distributionChannels} = this.props.product;
        let selectedOption = DISTRIBUTION.NONE;
        if (distributionChannels.shipping && this.props.isShopEnabled) {
            selectedOption = DISTRIBUTION.SHIPPING;
        }
        if (distributionChannels.mounting) {
            selectedOption = DISTRIBUTION.MOUNTING;
        }
        if (distributionChannels.pickup) {
            selectedOption = DISTRIBUTION.PICKUP;
        }
        this.state = {
            selectedOption,
            showInfo: false,
        };
    }

    setShowInfo(showInfo: boolean) {
        this.setState({showInfo});
    }

    selectOption(selectedOption: number) {
        this.setState({selectedOption});
    }

    render() {
        const shippingCost = this.props.product.distributionChannels.shipping
            ? currencyConverter(this.props.product.distributionChannels.shipping.shippingCost)
            : "";

        const getRecommendedRetailPrice = () => {
            switch (this.state.selectedOption) {
                case DISTRIBUTION.SHIPPING:
                    return shipping.recommendedRetailPrice;
                case DISTRIBUTION.PICKUP:
                    return pickup.recommendedRetailPrice;
                case DISTRIBUTION.MOUNTING:
                    return mounting.recommendedRetailPrice;
                default:
                    return this.props.product.recommendedRetailPrice;
            }
        };

        const getHistoricMinimalPrice = () => {
            switch (this.state.selectedOption) {
                case DISTRIBUTION.SHIPPING:
                    return shipping.historicMinimalPrice;
                case DISTRIBUTION.PICKUP:
                    return pickup.historicMinimalPrice;
                case DISTRIBUTION.MOUNTING:
                    return mounting.historicMinimalPrice;
                default:
                    return undefined;
            }
        }

        const label = {
            mounting: this.props.t("productDetail.assemblyPrice"),
            shipping: this.props.t("productDetail.shippingPrice"),
            pickup: this.props.t("productDetail.pickupPrice"),
            discount: this.props.t("productDetail.discountLabel"),
            priceWithVat: this.props.t("productDetail.inclVAT"),
            priceWithoutVat: this.props.t("productDetail.exclVAT"),
            priceWithShipping: this.props.t("productDetail.inclShipping"),
            priceWithoutShipping: this.props.t("productDetail.exclShipping", {shippingCost}),
            priceWithMounting: this.props.t("productDetail.inclMount"),
            priceWithoutMounting: this.props.t("productDetail.exclMount"),
            availableAtTrader: this.props.t("productDetail.availableAtTrader"),
            notAvailable: this.props.t("productDetail.notAvailable"),
            and: this.props.t("productDetail.and")
        };

        const {mounting, shipping, pickup} = this.props.product.distributionChannels;
        const {isAvailable, unitPrice} = this.props.product;
        const recommendedRetailPrice = getRecommendedRetailPrice();
        const price = getGalleryPrice(this.props.product, this.state.selectedOption, this.props.needsPickupLocation);
        const showAddToCartButton = ((mounting || shipping || pickup) && isAvailable && this.props.isShopEnabled === true);
        const showDisclaimer = !!price;
        const showContactButton = !this.props.watchlistEnabled && !showAddToCartButton;

        const getDiscountRate = () => {
            switch (this.state.selectedOption) {
                case DISTRIBUTION.MOUNTING:
                    return mounting.discountRate;
                case DISTRIBUTION.PICKUP:
                    return pickup.discountRate;
                case DISTRIBUTION.SHIPPING:
                    return shipping.discountRate;
                default:
                    return 0;
            }
        };

        const showShortText = (props: { minSupplyDays: number, maxSupplyDays: number }) => {
            return props.minSupplyDays <= 1 && props.maxSupplyDays <= 1;
        };

        const isShipping = this.state.selectedOption === DISTRIBUTION.SHIPPING;
        const isPickup = this.state.selectedOption === DISTRIBUTION.PICKUP;
        const isMounting = this.state.selectedOption === DISTRIBUTION.MOUNTING;
        const isShippingTimeVisible = isShipping && this.props.isShopEnabled;
        const isPickUpTimeVisible = isPickup && this.props.isShopEnabled;
        const isMountingTimeVisible = isMounting && this.props.isShopEnabled;
        const historicMinimalPrice = getHistoricMinimalPrice()
        const isDiscountRateVisible = historicMinimalPrice && this.state.selectedOption !== DISTRIBUTION.NONE && getDiscountRate() > 0;
        const priceNeedsASelectedLocation = this.props.needsPickupLocation && isMounting;

        let priceClass = classNames("hpm-price", {
            "hpm-sale-price": isDiscountRateVisible
        })
        let historicPriceClass = classNames("hpm-historic-price", {
            "hpm-stroke hpm-pushLeft": isDiscountRateVisible
        });
        let mountingClass = classNames("hpm-circle", {
            "hpm-selected": (this.state.selectedOption === DISTRIBUTION.MOUNTING)
        });
        let shippingClass = classNames("hpm-circle", {
            "hpm-selected": (this.state.selectedOption === DISTRIBUTION.SHIPPING)
        });
        let pickupClass = classNames("hpm-circle", {
            "hpm-selected": (this.state.selectedOption === DISTRIBUTION.PICKUP)
        });

        const renderAvailabilityInfo = () => {
            return (
                <div className="hpm-availability-info">
                {isShippingTimeVisible &&
                    <div className="hpm-info">
                        {showShortText(shipping)
                            ? this.props.t("productDetail.shippingDaysShort")
                            : this.props.t("productDetail.shippingDays", {
                                min: shipping.minSupplyDays,
                                max: shipping.maxSupplyDays
                            })
                        }
                    </div>
                }
                {isPickUpTimeVisible &&
                    <div className="hpm-info">
                        {showShortText(pickup)
                            ? this.props.t("productDetail.pickupDaysShort")
                            : this.props.t("productDetail.pickupDays", {
                                min: pickup.minSupplyDays,
                                max: pickup.maxSupplyDays
                            })
                        }
                    </div>
                }
                {isMountingTimeVisible &&
                    <div className="hpm-info">
                        {showShortText(mounting)
                            ? this.props.t("productDetail.mountingDaysShort")
                            : this.props.t("productDetail.mountingDays", {
                                min: mounting.minSupplyDays,
                                max: mounting.maxSupplyDays
                            })
                        }
                    </div>
                }
                {isStockLimited(this.props.product) && isAvailable &&
                    <div className="hpm-info hpm-limited-stock">
                        {this.props.t("productDetail.limitedStock", {count: this.props.product.availableQuantity})}
                    </div>
                }
                </div>);
        }

        const renderPriceBox = () => {
            const showMsrp = this.props.showMsrp;

            return (
                <div className="hpm-priceBoxLabel">
                    <div className="hpm-priceBox-price">
                        {priceNeedsASelectedLocation
                            ? this.props.t("productDetail.selectLocation")
                            : price
                                ? (<>
                                    <div className={"hpm-flex-row"}>
                                        <span className={priceClass}>{currencyConverter(price)}</span>
                                        <span className="hpm-info">{price && label.priceWithVat}</span>
                                    </div>
                                        {unitPrice && (
                                            <div className="unit-price">
                                                <small>
                                                    ({currencyConverter(unitPrice.pricePerUnit)} / {unitPrice.measuringUnit === "sqm" ? "m²" : unitPrice.measuringUnit === "cbm" ? "m³" : unitPrice.measuringUnit})
                                                </small>
                                            </div>
                                        )}
                                </>)
                                : (
                                    this.props.t("productDetail.priceOnRequest")
                                )
                        }
                    {!priceNeedsASelectedLocation &&
                        <div className="hpm-info">
                            {isPickup && <>&nbsp;</>}
                            {isShipping && <React.Fragment>{label.priceWithoutShipping}</React.Fragment>}
                            {isMounting && <React.Fragment>{label.priceWithMounting}</React.Fragment>}
                        </div>
                    }
                    </div>
                    {historicMinimalPrice &&
                        <div className={"hpm-historic-box"}>
                            <span>{this.props.t("productDetail.historicMinimalPurchasePrice")} </span>
                            <div className={"hpm-flex-row"}>
                                <PriceInfo
                                    t={this.props.t}
                                    price={price}
                                    recommendedRetailPrice={recommendedRetailPrice}
                                    historicMinimalPurchasePrice={historicMinimalPrice}
                                    showInfo={this.state.showInfo}
                                    setShowInfo={(show) => this.setShowInfo(show)}
                                    showMsrp={showMsrp}
                                />
                                <span className={historicPriceClass}>{currencyConverter(historicMinimalPrice)}</span>
                                {isDiscountRateVisible &&
                                    <span className="hpm-discount"> -{getDiscountRate()}%</span>
                                }
                            </div>
                        </div>
                    }
                    {(!!showMsrp && recommendedRetailPrice) &&
                        <div className={"hpm-retailPrice"}>
                            {this.props.t("productDetail.recommendedRetailPrice")}: <span className="hpm-nowrap">{currencyConverter(recommendedRetailPrice)}</span>
                        </div>
                    }
                </div>
            );
        }

        const renderContactButton = () => {
            return <Button name="cartButton primary" id="hpmt-contactButton">
                <a href={this.props.dealerDetails.phone
                    ? `tel:${this.props.dealerDetails.phone.replace(/ /g, "")}`
                    // eslint-disable-next-line no-script-url
                    : "javascript:void(0)"}
                   onClick={() => this.props.dealerDetails.phone && emitInternalEvent("call_dealer", {
                       product: toProductDetails(this.props.product),
                       phone: `tel:${this.props.dealerDetails.phone.replace(/ /g, "")}`,
                   })}
                >
                    {this.props.dealerDetails.phone
                        ? `${this.props.t("productDetail.callMe")} (${StringUtils.makeSpacesNonBreaking(this.props.dealerDetails.phone)})`
                        : ""
                    }
                </a>
            </Button>;
        };

        const renderWatchlistButton = () => {
            const isShopEnabled = this.props.isShopEnabled;
            const isSelected = this.props.isBookmarked;

            return (
                <button
                    aria-label={isSelected ? this.props.t("productDetail.removeFromBookmarks") : this.props.t("productDetail.addToBookmarks")}
                    id="hpmt-bookmarkButton"
                    type="button"
                    className={`hpm-cartButton bookmarks ${
                        isShopEnabled && !mounting && !shipping && !pickup ? "isShopDisable" : isShopEnabled ? "isShopEnable" : "isShopDisable"
                    } ${isSelected ? "selected" : "not-selected"}`}
                    onClick={() => {
                        isSelected ? this.props.removeBookmark(this.props.product) : this.props.addBookmark(this.props.product);
                    }}
                    aria-pressed={isSelected}
                >
                    <span className="ico"/>
                    <span>{isSelected ? this.props.t("productDetail.removeFromBookmarks") : this.props.t("productDetail.addToBookmarks")}</span>
                </button>
            );
        };
        const XOR = (a, b) => {
            return (a && !b) || (!a && b);
        };

        const showShippingTypeSelector = !XOR(XOR(mounting, shipping), pickup) || this.props.isShopEnabled === true;

        return (
            <div className="hpm-priceBox" data-testid="priceBox">
                <div className="hpm-price-row">
                    <div className="hpm-menu">
                        {pickup &&
                            <div
                                className="hpm-menuElement"
                                onClick={() => this.selectOption(DISTRIBUTION.PICKUP)}
                                onKeyDown={e => e.key === "Enter" && this.selectOption(DISTRIBUTION.PICKUP)}
                                role="button"
                                tabIndex={0}
                                aria-label={label.pickup}
                            >
                                {showShippingTypeSelector && <React.Fragment>
                                    <div className={pickupClass}/>
                                    <div className="hpm-optionText">{label.pickup}</div>
                                </React.Fragment>}
                            </div>}
                        {mounting &&
                            <div
                                className="hpm-menuElement"
                                onClick={() => this.selectOption(DISTRIBUTION.MOUNTING)}
                                onKeyDown={e => e.key === "Enter" && this.selectOption(DISTRIBUTION.MOUNTING)}
                                role="button"
                                tabIndex={0}
                                aria-label={label.mounting}
                            >
                                {showShippingTypeSelector && <React.Fragment>
                                    <div className={mountingClass}/>
                                    <div className="hpm-optionText">{label.mounting}</div>
                                </React.Fragment>}
                            </div>}
                        {(shipping && this.props.isShopEnabled) &&
                            <div
                                className="hpm-menuElement"
                                onClick={() => this.selectOption(DISTRIBUTION.SHIPPING)}
                                onKeyDown={e => e.key === "Enter" && this.selectOption(DISTRIBUTION.SHIPPING)}
                                role="button"
                                tabIndex={0}
                                aria-label={label.shipping}
                            >
                                {showShippingTypeSelector && <React.Fragment>
                                    <div className={shippingClass}/>
                                    <div className="hpm-optionText">{label.shipping}</div>
                                </React.Fragment>}
                            </div>}

                        {(this.props.isShopEnabled && !shipping && !mounting && !pickup)
                            ? isAvailable
                                ? label.availableAtTrader
                                : label.notAvailable
                            : null
                        }
                        {renderAvailabilityInfo()}
                    </div>
                    {renderPriceBox()}
                </div>
                <div className="hpm-clear" />
                <div className="hpm-actions">
                    {(showAddToCartButton) && ((this.props.needsPickupLocation && !isShipping)
                            ? <div className="hpm-cartButton inverted">
                                <PickupLocationContainer
                                    linkStyle={{
                                        display: "block",
                                        width: "100%"
                                    }}
                                    linkLabel={isMounting ? this.props.t("location.locationForMount") : this.props.t("location.locationForPickup")}
                                />
                            </div>
                            : <Button
                                onClick={() => this.props.addToCart(this.state.selectedOption)}
                                name="cartButton primary"
                                id="hpmt-cartButton">
                                {this.props.t("productDetail.addToCart")}
                            </Button>
                    )}
                    {(!showAddToCartButton && this.props.watchlistEnabled && this.props.needsPickupLocation) &&
                        <div className="hpm-cartButton inverted">
                            <PickupLocationContainer
                                linkStyle={{
                                    display: "block",
                                    width: "100%"
                                }}
                                linkLabel={this.props.t("location.headline")}
                            />
                        </div>}
                    {(this.props.watchlistEnabled && !this.props.needsPickupLocation) && renderWatchlistButton()}
                    {showContactButton && renderContactButton()}
                </div>
                {showDisclaimer &&
                    <div className="hpm-infoBoxPrice" dangerouslySetInnerHTML={{__html: this.props.infobox}}/>
                }
            </div>
        );
    }
};

export function AvailabilityLabel({
                                      t,
                                      product,
                                      selectedOption,
                                      needsPickupLocation,
                                      isShopEnabled
                                  }) {
    const {
        mounting,
        shipping,
        pickup
    } = product.distributionChannels;
    const price = getGalleryPrice(product, selectedOption, needsPickupLocation);

    const label = {
        priceWithVat: t("productDetail.inclVAT"),
        priceWithoutShipping: t("productDetail.exclShipping", {
            shippingCost: shipping
                ? currencyConverter(shipping.shippingCost)
                : ""
        }),
        priceWithMounting: t("productDetail.inclMount"),
        and: t("productDetail.and"),
        priceOnRequest: t("productDetail.priceOnRequest"),
        selectLocation: t("productDetail.selectLocation"),
        shippingDaysShort: t("productDetail.shippingDaysShort"),
        shippingDays: t("productDetail.shippingDays", {
            min: shipping?.minSupplyDays || "",
            max: shipping?.maxSupplyDays || ""
        }),
        pickupDaysShort: t("productDetail.pickupDaysShort"),
        pickupDays: t("productDetail.pickupDays", {
            min: pickup?.minSupplyDays || "",
            max: pickup?.maxSupplyDays || ""
        }),
        mountingDaysShort: t("productDetail.mountingDaysShort"),
        mountingDays: t("productDetail.mountingDays", {
            min: mounting?.minSupplyDays || "",
            max: mounting?.maxSupplyDays || ""
        }),
    };

    const showShortText = (props) => props?.minSupplyDays <= 1 && props?.maxSupplyDays <= 1;
    const isShipping = selectedOption === DISTRIBUTION.SHIPPING;
    const isPickup = selectedOption === DISTRIBUTION.PICKUP;
    const isMounting = selectedOption === DISTRIBUTION.MOUNTING;
    const isShippingTimeVisible = isShipping && isShopEnabled;
    const isPickUpTimeVisible = isPickup && isShopEnabled;
    const isMountingTimeVisible = isMounting && isShopEnabled;
    const priceNeedsASelectedLocation = needsPickupLocation && isMounting;

    return (
        <div>
            {!priceNeedsASelectedLocation &&
                <div className="hpm-info">
                    <span>{currencyConverter(price)} {label.priceWithVat}</span>
                    {isShipping && <span>{label.priceWithoutShipping}</span>}
                    {isMounting && <span>{label.priceWithMounting}</span>}
                </div>
            }
            {isShippingTimeVisible &&
                <div className="hpm-info">
                    {showShortText(shipping)
                        ? label.shippingDaysShort
                        : label.shippingDays
                    }
                </div>}
            {isPickUpTimeVisible &&
                <div className="hpm-info">
                    {showShortText(pickup)
                        ? label.pickupDaysShort
                        : label.pickupDays
                    }
                </div>}
            {isMountingTimeVisible &&
                <div className="hpm-info">
                    {showShortText(mounting)
                        ? label.mountingDaysShort
                        : label.mountingDays
                    }
                </div>}
        </div>
    );
}
